import React, { useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Input,
  Select,
  SimpleGrid,
  Spinner,
  Stack,
  Text,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { Bug, RefreshCw, Search } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import GenericTable from '../components/GenericTable';
import { getCurrentDraws, getPendingDraws } from '../actions/drawActions';
import styles from '../styles/ManagementTable.module.css';

const baseUrl = process.env.REACT_APP_API_URL_ADMIN;

const formatMoney = (value) => {
  if (value == null || value === '') return '—';
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString(undefined, { maximumFractionDigits: 2 });
};

const formatNum = (value) => {
  if (value == null || value === '') return '—';
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value);
  return n.toLocaleString(undefined, { maximumFractionDigits: 3 });
};

const outcomeBadge = (outcome) => {
  switch (outcome) {
    case 'win':
      return { colorScheme: 'green', label: 'Win' };
    case 'blocked':
      return { colorScheme: 'red', label: 'Blocked' };
    case 'no_win':
    default:
      return { colorScheme: 'gray', label: 'No win' };
  }
};

const stateBadge = (state) => {
  const map = {
    W: { colorScheme: 'purple', label: 'W' },
    H: { colorScheme: 'orange', label: 'H' },
    M: { colorScheme: 'blue', label: 'M' },
    L: { colorScheme: 'gray', label: 'L' },
  };
  return map[state] || { colorScheme: 'gray', label: state || '—' };
};

const EngineDebug = () => {
  const dispatch = useDispatch();
  const toast = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const { currentDraws, pendingDraws } = useSelector((state) => state.draw);

  const [drawId, setDrawId] = useState(searchParams.get('draw_id') || '');
  const [net2payScope, setNet2payScope] = useState('per_game');
  const [outcomeFilter, setOutcomeFilter] = useState('all');
  const [playerQuery, setPlayerQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [payload, setPayload] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    dispatch(getCurrentDraws());
    dispatch(getPendingDraws());
  }, [dispatch]);

  useEffect(() => {
    const fromQuery = searchParams.get('draw_id');
    if (fromQuery && fromQuery !== drawId) {
      setDrawId(fromQuery);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const quickPickOptions = useMemo(() => {
    const rows = [...(currentDraws || []), ...(pendingDraws || [])];
    const seen = new Set();
    const out = [];
    for (const d of rows) {
      const id = String(d.draw_id || '');
      if (!id || seen.has(id)) continue;
      seen.add(id);
      out.push({
        draw_id: id,
        label: `${id} · ${d.game_name || d.gameNumber || d.game_id || 'game'} · ${
          d.result ? `result ${d.result}` : d.status || 'open'
        }`,
      });
    }
    return out.slice(0, 40);
  }, [currentDraws, pendingDraws]);

  const analyze = async (idOverride) => {
    const id = String(idOverride || drawId || '').trim();
    if (!id) {
      toast({
        title: 'Draw ID required',
        status: 'warning',
        duration: 2500,
        isClosable: true,
      });
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await axios.post(`${baseUrl}`, {
        apiName: 'draw',
        command: 'getEngineDebug',
        drawId: id,
        net2payScope,
      });
      const body = response.data;
      if (body.status && body.status !== 200) {
        throw new Error(body.message || 'Engine debug failed');
      }
      setPayload(body.data || null);
      setDrawId(id);
      setSearchParams({ draw_id: id });
    } catch (err) {
      const message =
        err.response?.data?.message || err.message || 'Failed to load engine debug';
      setError(message);
      setPayload(null);
      toast({
        title: 'Engine debug failed',
        description: message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const initial = searchParams.get('draw_id');
    if (initial) {
      analyze(initial);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const decision = payload?.decision;
  const playerBoard = payload?.player_board || [];

  const filteredBoard = useMemo(() => {
    const q = playerQuery.trim().toLowerCase();
    return playerBoard.filter((row) => {
      if (outcomeFilter !== 'all' && row.outcome !== outcomeFilter) return false;
      if (!q) return true;
      return (
        String(row.player_id).toLowerCase().includes(q) ||
        String(row.username || '')
          .toLowerCase()
          .includes(q) ||
        String(row.why || '')
          .toLowerCase()
          .includes(q)
      );
    });
  }, [playerBoard, outcomeFilter, playerQuery]);

  const columns = useMemo(
    () => [
      {
        Header: 'Player',
        accessor: 'username',
        Cell: ({ row }) => (
          <Box>
            <Text fontWeight="600">{row.original.username}</Text>
            {row.original.username !== row.original.player_id ? (
              <Text fontSize="xs" color="gray.500">
                {row.original.player_id}
              </Text>
            ) : null}
          </Box>
        ),
      },
      {
        Header: 'State',
        accessor: 'state',
        Cell: ({ value }) => {
          const b = stateBadge(value);
          return <Badge colorScheme={b.colorScheme}>{b.label}</Badge>;
        },
      },
      {
        Header: 'Manual NTP',
        accessor: 'manual_ntp',
        Cell: ({ value }) => value || '—',
      },
      {
        Header: 'Outcome',
        accessor: 'outcome',
        Cell: ({ value }) => {
          const b = outcomeBadge(value);
          return <Badge colorScheme={b.colorScheme}>{b.label}</Badge>;
        },
      },
      {
        Header: 'This-draw win',
        accessor: 'position_win_amount',
        Cell: ({ value }) => formatMoney(value),
      },
      {
        Header: 'Sales',
        accessor: 'sales',
        Cell: ({ value }) => formatMoney(value),
      },
      {
        Header: 'Current win',
        accessor: 'current_win',
        Cell: ({ value }) => formatMoney(value),
      },
      {
        Header: 'Available',
        accessor: 'available_win',
        Cell: ({ value }) => formatMoney(value),
      },
      {
        Header: 'Effective limit',
        accessor: 'effective_win_limit',
        Cell: ({ value }) => formatMoney(value),
      },
      {
        Header: 'Target NTP',
        accessor: 'target_ntp',
        Cell: ({ value }) => formatNum(value),
      },
      {
        Header: 'Projected NTP',
        accessor: 'projected_ntp',
        Cell: ({ value }) => formatNum(value),
      },
      {
        Header: 'Why',
        accessor: 'why',
        defaultWidth: 280,
        Cell: ({ value }) => (
          <Text fontSize="sm" whiteSpace="normal" maxW="360px">
            {value || '—'}
          </Text>
        ),
      },
    ],
    []
  );

  return (
    <div className={styles.managementPage}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <Bug className={styles.headerIcon} size={28} />
          <h1 className={styles.title}>Draw Engine Debug</h1>
        </div>
        <Text color="gray.600" fontSize="sm">
          View-only counterfactual · who wins / who does not · why
        </Text>
      </div>

      <Box
        bg="white"
        borderRadius="md"
        border="1px solid"
        borderColor="gray.200"
        p={4}
        mb={4}
      >
        <Stack spacing={4}>
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
            <FormControl>
              <FormLabel fontSize="sm">Draw ID</FormLabel>
              <Input
                value={drawId}
                onChange={(e) => setDrawId(e.target.value)}
                placeholder="Enter draw_id"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') analyze();
                }}
              />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="sm">Quick pick (current / pending)</FormLabel>
              <Select
                placeholder="Select a live draw"
                value=""
                onChange={(e) => {
                  if (e.target.value) {
                    setDrawId(e.target.value);
                    analyze(e.target.value);
                  }
                }}
              >
                {quickPickOptions.map((opt) => (
                  <option key={opt.draw_id} value={opt.draw_id}>
                    {opt.label}
                  </option>
                ))}
              </Select>
            </FormControl>
            <FormControl>
              <FormLabel fontSize="sm">Net-to-pay scope</FormLabel>
              <Select value={net2payScope} onChange={(e) => setNet2payScope(e.target.value)}>
                <option value="per_game">per_game</option>
                <option value="all_games">all_games</option>
              </Select>
            </FormControl>
          </SimpleGrid>
          <HStack>
            <Button
              leftIcon={loading ? <Spinner size="sm" /> : <Search size={16} />}
              colorScheme="blue"
              onClick={() => analyze()}
              isDisabled={loading}
            >
              Analyze (view)
            </Button>
            <Button
              leftIcon={<RefreshCw size={16} />}
              variant="outline"
              onClick={() => {
                dispatch(getCurrentDraws());
                dispatch(getPendingDraws());
                if (drawId) analyze();
              }}
              isDisabled={loading}
            >
              Refresh
            </Button>
          </HStack>
        </Stack>
      </Box>

      {error ? (
        <Box bg="red.50" border="1px solid" borderColor="red.200" p={4} borderRadius="md" mb={4}>
          <Text color="red.700">{error}</Text>
        </Box>
      ) : null}

      {loading && !payload ? (
        <Flex justify="center" py={16}>
          <Spinner size="lg" />
        </Flex>
      ) : null}

      {payload ? (
        <VStack align="stretch" spacing={4}>
          <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={3}>
            <StatCard
              label="Picked cell"
              value={
                decision?.best_position != null
                  ? `${decision.best_position} × ${decision.best_bonus ?? '?'}x`
                  : 'N/A'
              }
              sub={
                decision?.best_score != null
                  ? `score ${formatNum(decision.best_score)} · payout ${formatMoney(
                      decision.best_win_amount
                    )}`
                  : payload.engine?.game_name || ''
              }
            />
            <StatCard
              label="Winners"
              value={String(decision?.winners_count ?? 0)}
              sub={(decision?.winner_ids || []).slice(0, 6).join(', ') || 'none'}
              accent="green"
            />
            <StatCard
              label="Blocked / no-win"
              value={`${decision?.blocked_count ?? 0} / ${decision?.no_win_count ?? 0}`}
              sub={decision?.selection_reason || '—'}
              accent="orange"
            />
            <StatCard
              label="Published vs pick"
              value={
                payload.draw?.result
                  ? String(payload.draw.result)
                  : 'Not published'
              }
              sub={
                payload.draw?.result && decision?.best_position
                  ? String(payload.draw.result) === String(decision.best_position)
                    ? 'Matches engine pick'
                    : `Engine would pick ${decision.best_position}`
                  : `Scope ${payload.net2pay_scope || net2payScope}`
              }
            />
          </SimpleGrid>

          <Box
            bg="white"
            borderRadius="md"
            border="1px solid"
            borderColor="gray.200"
            p={4}
          >
            <HStack justify="space-between" mb={3} flexWrap="wrap" gap={2}>
              <Box>
                <Heading size="sm">Player win board</Heading>
                <Text fontSize="sm" color="gray.600">
                  {payload.engine?.game_name || payload.draw?.game_name || 'Game'} · draw{' '}
                  {payload.drawId}
                  {payload.engine?.favour_matrix_enabled ? ' · favour matrix on' : ''}
                  {payload.target ? ` · via ${payload.target}` : ''}
                </Text>
              </Box>
              <HStack>
                <Select
                  size="sm"
                  maxW="160px"
                  value={outcomeFilter}
                  onChange={(e) => setOutcomeFilter(e.target.value)}
                >
                  <option value="all">All outcomes</option>
                  <option value="win">Win only</option>
                  <option value="blocked">Blocked only</option>
                  <option value="no_win">No-win only</option>
                </Select>
                <Input
                  size="sm"
                  maxW="220px"
                  placeholder="Filter player / reason"
                  value={playerQuery}
                  onChange={(e) => setPlayerQuery(e.target.value)}
                />
              </HStack>
            </HStack>

            {(payload.warnings || []).length > 0 ? (
              <Box mb={3} p={3} bg="yellow.50" borderRadius="md" border="1px solid" borderColor="yellow.200">
                {(payload.warnings || []).map((w, i) => (
                  <Text key={i} fontSize="sm" color="yellow.800">
                    {w}
                  </Text>
                ))}
              </Box>
            ) : null}

            {payload.high_selection ? (
              <Box mb={3} p={3} bg="orange.50" borderRadius="md" fontSize="sm">
                <Text fontWeight="600">HIGH selection audit</Text>
                <Text>
                  Satisfied: {(payload.high_selection.satisfied_high_player_ids || []).join(', ') || '—'}
                  {' · '}
                  Pending: {(payload.high_selection.pending_high_player_ids || []).join(', ') || '—'}
                </Text>
                {(payload.high_selection.high_reject_reasons || []).length > 0 ? (
                  <Text color="orange.800">
                    Rejects: {(payload.high_selection.high_reject_reasons || []).join('; ')}
                  </Text>
                ) : null}
              </Box>
            ) : null}

            {payload.forced_ntp_selection ? (
              <Box mb={3} p={3} bg="purple.50" borderRadius="md" fontSize="sm">
                <Text fontWeight="600">
                  Forced NTP · {payload.forced_ntp_selection.status || '—'}
                  {payload.forced_ntp_selection.selected_position
                    ? ` · ${payload.forced_ntp_selection.selected_position}×${payload.forced_ntp_selection.selected_bonus}x`
                    : ''}
                </Text>
                <Text>
                  Satisfied:{' '}
                  {(payload.forced_ntp_selection.satisfied_player_ids || []).join(', ') || '—'}
                </Text>
              </Box>
            ) : null}

            <GenericTable
              columns={columns}
              data={filteredBoard}
              initialPageSize={50}
              key={`${payload.drawId}-${outcomeFilter}-${playerQuery}`}
            />
          </Box>
        </VStack>
      ) : null}
    </div>
  );
};

function StatCard({ label, value, sub, accent }) {
  const border =
    accent === 'green' ? 'green.200' : accent === 'orange' ? 'orange.200' : 'gray.200';
  const bg =
    accent === 'green' ? 'green.50' : accent === 'orange' ? 'orange.50' : 'white';
  return (
    <Box bg={bg} border="1px solid" borderColor={border} borderRadius="md" p={4}>
      <Text fontSize="xs" textTransform="uppercase" letterSpacing="0.08em" color="gray.500">
        {label}
      </Text>
      <Text fontSize="xl" fontWeight="700" mt={1} wordBreak="break-word">
        {value}
      </Text>
      <Text fontSize="sm" color="gray.600" mt={1} noOfLines={2}>
        {sub}
      </Text>
    </Box>
  );
}

export default EngineDebug;
